import React, { useEffect } from 'react';
import { Drawer, Form, Button, SelectPicker, Message } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { getServicelines } from '@/services/serviceline.service';
import { useTranslation } from 'react-i18next';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    gpn: string;
    sl: string;
  };
  users: User[];
  usersLoading: boolean;
  toaster: any;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  usersLoading,
  toaster
}) => {
  const dispatch = useDispatch();
  const { data: servicelines, loading: servicelineLoading } = useSelector(
    state => state.serviceLine
  );
  const [formValue, setFormValue] = React.useState(currentFilters);

  const userOptions = React.useMemo(() => {
    return users.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  useEffect(() => {
    dispatch(getServicelines());
  }, [dispatch]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        gpn: formValue.gpn?.toLowerCase(),
        sl: formValue.sl
      });
      toaster.push(
        <Message type="success" closable>
          {t('Filters applied successfully')}
        </Message>
      );
    } else {
      onResetFilters();
    }

    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      gpn: '',
      sl: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" closable>
        Filters have been reset
      </Message>
    );
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);
  const { t } = useTranslation();






  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t("Select user")}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Team')}</Form.ControlLabel>
            <Form.Control
              name="sl"
              accepter={SelectPicker}
              data={
                servicelines?.map(sl => ({
                  label: sl.serviceLine,
                  value: sl.serviceLine
                })) || []
              }
              loading={servicelineLoading}
              searchable
              placeholder="Select Team"
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
